/** Deferred post-automation: create project → workshop → open / auto-publish after upload. */
export const OPEN_POST_AUTOMATION_STORAGE_KEY = 'thspian_open_post_automation';

export type SocialPlatformKey = 'instagram' | 'tiktok' | 'youtube';

/** Draft collected in Create Project when Post Automation is on. */
export type PostAutomationDraft = {
    platforms: Partial<Record<SocialPlatformKey, boolean>>;
    scheduleMode: 'now' | 'schedule';
    /** ISO datetime when scheduleMode === 'schedule' */
    scheduledAt?: string;
    /** Activate publish as soon as media is ready (subscription already gated at create). */
    activateImmediately?: boolean;
};

export type PostAutomationIntent =
    | { status: 'pending'; draft?: PostAutomationDraft }
    | { status: 'bound'; projectId: string; draft?: PostAutomationDraft }
    | { status: 'ready'; projectId: string; draft?: PostAutomationDraft };

function readIntent(): PostAutomationIntent | null {
    try {
        const raw = sessionStorage.getItem(OPEN_POST_AUTOMATION_STORAGE_KEY);
        if (!raw) return null;
        if (raw === 'pending' || raw === '1') return { status: 'pending' };
        const parsed = JSON.parse(raw) as PostAutomationIntent;
        if (parsed?.status === 'pending') return parsed;
        if ((parsed?.status === 'bound' || parsed?.status === 'ready') && parsed.projectId) {
            return parsed;
        }
    } catch {
        // ignore
    }
    return null;
}

function writeIntent(intent: PostAutomationIntent | null) {
    try {
        if (!intent) {
            sessionStorage.removeItem(OPEN_POST_AUTOMATION_STORAGE_KEY);
            return;
        }
        sessionStorage.setItem(OPEN_POST_AUTOMATION_STORAGE_KEY, JSON.stringify(intent));
    } catch {
        // ignore quota / private mode
    }
}

/** Call when user enables Post Automation on Create Project (project not created yet). */
export function setPendingPostAutomationIntent(enabled: boolean, draft?: PostAutomationDraft) {
    if (!enabled) {
        writeIntent(null);
        return;
    }
    writeIntent({
        status: 'pending',
        draft: draft ?? {
            platforms: { instagram: true, tiktok: true, youtube: true },
            scheduleMode: 'now',
            activateImmediately: true,
        },
    });
}

/** Update draft while still on Create Project (pending) or after bind. */
export function updatePostAutomationDraft(draft: PostAutomationDraft) {
    const current = readIntent();
    if (!current) {
        writeIntent({ status: 'pending', draft });
        return;
    }
    writeIntent({ ...current, draft });
}

export function getPostAutomationDraft(): PostAutomationDraft | null {
    return readIntent()?.draft ?? null;
}

/**
 * Project document created — bind intent and navigate to workshop.
 * @returns true if caller should navigate to workshop for this project
 */
export function bindPostAutomationProject(projectId: string): boolean {
    const current = readIntent();
    if (!projectId) return false;
    if (!current || current.status !== 'pending') return false;
    writeIntent({ status: 'bound', projectId: String(projectId), draft: current.draft });
    return true;
}

/** Media upload finished — automation may open once media_url is present. */
export function markPostAutomationReadyForProject(projectId: string): boolean {
    if (!projectId) return false;
    const current = readIntent();
    if (!current) return false;
    const id = String(projectId);
    if (current.status === 'pending') {
        writeIntent({ status: 'ready', projectId: id, draft: current.draft });
        return true;
    }
    if ((current.status === 'bound' || current.status === 'ready') && current.projectId === id) {
        writeIntent({ status: 'ready', projectId: id, draft: current.draft });
        return true;
    }
    return false;
}

/** Workshop: this project should open automation once media is available. */
export function shouldOpenPostAutomationForProject(projectId?: string | null): boolean {
    if (!projectId) return false;
    const current = readIntent();
    if (!current || current.status === 'pending') return false;
    return (current.status === 'bound' || current.status === 'ready') && current.projectId === String(projectId);
}

export function shouldAutoPublishPostAutomation(projectId?: string | null): boolean {
    if (!shouldOpenPostAutomationForProject(projectId)) return false;
    const draft = getPostAutomationDraft();
    return Boolean(draft?.activateImmediately);
}

/** Clear after automation modal is opened (or user cancels the flow). */
export function clearPostAutomationIntent() {
    writeIntent(null);
}
